import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Download as DownloadIcon, Key, FileText, CircleNotch, XCircle, Copy, Lightning, ArrowSquareOut, Package, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DownloadPage() {
  const { grantId } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    api.get(`/download/${grantId}`, { withCredentials: false })
      .then(r => setState({ loading: false, error: null, data: r.data }))
      .catch(e => setState({
        loading: false,
        error: e.response?.data?.detail || "Download link not found",
        data: null
      }));
  }, [grantId]);

  const copy = (v, label) => {
    navigator.clipboard.writeText(v);
    toast.success(`${label} copied`);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-arena-bg text-arena-text font-mono flex items-center justify-center">
        <CircleNotch size={32} weight="bold" className="animate-spin text-arena-primary" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-arena-bg text-arena-text font-mono flex items-center justify-center p-8">
        <div className="max-w-md w-full border border-arena-danger bg-arena-surface p-8 text-center">
          <XCircle size={40} weight="fill" className="text-arena-danger mx-auto mb-3" />
          <div className="font-heading text-2xl font-bold">DOWNLOAD UNAVAILABLE</div>
          <div className="text-arena-muted text-xs mt-2">{state.error}</div>
          <Link to="/" className="inline-block mt-6 text-xs tracking-widest uppercase text-arena-primary hover:underline">← Home</Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  const hasUrl = d.delivery_type === "url" && d.delivery_url;
  const hasLicense = d.delivery_type === "license" && d.delivery_license;
  const hasText = d.delivery_type === "text" && d.delivery_text;
  const notConfigured = !hasUrl && !hasLicense && !hasText;

  return (
    <div className="min-h-screen bg-arena-bg text-arena-text font-mono" data-testid="download-page">
      {/* Header */}
      <header className="border-b border-arena-border p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning size={20} weight="fill" className="text-arena-primary" />
            <span className="font-heading font-bold tracking-tight">PEP<span className="text-arena-primary">.</span>STYLES</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-arena-muted">
            <ShieldCheck size={14} weight="bold" className="text-arena-success" />
            Secure delivery
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6">
        {/* Success banner */}
        <div className="mb-6">
          <div className="overline text-arena-success mb-2">// order delivered</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">
            YOUR <span className="text-arena-primary">DOWNLOAD</span> IS READY
          </h1>
          <p className="text-arena-muted text-sm mt-3">
            Order <span className="text-arena-primary font-bold">{d.order_number}</span>
            {d.customer_name ? ` for ${d.customer_name}` : ""} — thanks for the purchase.
          </p>
        </div>

        {/* Product card */}
        <div className="border border-arena-border bg-arena-surface p-6 mb-4">
          <div className="overline mb-2">// item</div>
          <div className="font-heading text-xl font-bold">{d.product_name}</div>
        </div>

        {/* Delivery block */}
        {notConfigured && (
          <div className="border border-arena-warning bg-arena-surface p-6 mb-4" data-testid="delivery-preparing">
            <div className="flex items-start gap-3">
              <Package size={20} weight="bold" className="text-arena-warning mt-0.5" />
              <div>
                <div className="font-heading text-lg font-bold text-arena-warning">DELIVERY PENDING</div>
                <div className="text-xs text-arena-muted mt-2 leading-relaxed">
                  Your payment went through, but the download for this product isn&apos;t configured yet. The store owner has been notified and will email you the download link within 24 hours. Please contact <span className="text-arena-primary">support@pepstyles.store</span> if you don&apos;t hear back.
                </div>
              </div>
            </div>
          </div>
        )}

        {hasUrl && (
          <div className="border border-arena-border bg-arena-surface p-6 mb-4" data-testid="delivery-url-block">
            <div className="overline mb-3 text-arena-primary">// download link</div>
            <a
              href={d.delivery_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-button"
              className="block bg-arena-primary text-black px-8 py-6 text-center font-heading text-2xl font-bold hover:bg-arena-primaryHover transition-colors"
            >
              <DownloadIcon size={28} weight="bold" className="inline mb-1" />
              <div>DOWNLOAD NOW</div>
              <div className="text-xs tracking-widest font-mono mt-2 opacity-80">
                Opens in a new tab
              </div>
            </a>
            <div className="mt-4 text-[10px] text-arena-muted flex items-center gap-2">
              <ArrowSquareOut size={12} weight="bold" />
              Direct link: <code className="text-arena-primary truncate flex-1">{d.delivery_url}</code>
              <button onClick={() => copy(d.delivery_url, "Link")} className="hover:text-arena-primary" data-testid="copy-download-url">
                <Copy size={12} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {hasLicense && (
          <div className="border border-arena-border bg-arena-surface p-6 mb-4" data-testid="delivery-license-block">
            <div className="overline mb-3 text-arena-primary">// your license key</div>
            <div className="bg-black border border-arena-border p-4 font-mono text-xl text-arena-primary tracking-widest text-center relative">
              {d.delivery_license}
              <button onClick={() => copy(d.delivery_license, "License")} className="absolute top-2 right-2 text-arena-muted hover:text-arena-primary" data-testid="copy-license">
                <Copy size={14} weight="bold" />
              </button>
            </div>
            <div className="mt-3 text-[10px] text-arena-muted flex items-center gap-2">
              <Key size={12} weight="bold" />
              Keep this key safe. Redeem instructions below if provided.
            </div>
          </div>
        )}

        {hasText && (
          <div className="border border-arena-border bg-arena-surface p-6 mb-4" data-testid="delivery-text-block">
            <div className="overline mb-3 text-arena-primary">// your content</div>
            <div className="bg-black border border-arena-border p-5 relative">
              <div className="whitespace-pre-wrap text-sm text-arena-text leading-relaxed pr-8">{d.delivery_text}</div>
              <button onClick={() => copy(d.delivery_text, "Content")} className="absolute top-3 right-3 text-arena-muted hover:text-arena-primary" data-testid="copy-text">
                <Copy size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {d.delivery_instructions && (
          <div className="border border-arena-border bg-arena-surface p-5 mb-4">
            <div className="overline mb-2">// instructions</div>
            <div className="text-sm text-arena-text whitespace-pre-wrap">{d.delivery_instructions}</div>
          </div>
        )}

        {/* Meta */}
        <div className="border border-arena-border bg-arena-surface p-5 text-[10px] text-arena-muted grid grid-cols-3 gap-4">
          <div>
            <div className="overline mb-1">accesses</div>
            <div className="text-arena-text font-heading">{d.access_count} / {d.max_accesses}</div>
          </div>
          <div>
            <div className="overline mb-1">expires</div>
            <div className="text-arena-text font-heading">
              {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "Never"}
            </div>
          </div>
          <div>
            <div className="overline mb-1">status</div>
            <div className="text-arena-success font-heading">ACTIVE</div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <div className="text-[10px] text-arena-muted">
            Save this page — you can return here anytime with the same link.
          </div>
        </div>
      </div>
    </div>
  );
}
